import { useNavigateWithParams } from '../hooks/useNavigateWithParams';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Sparkles, FileText, Target, Settings, Library } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';

export function Welcome() {
  const navigate = useNavigateWithParams();

  const features = [
    {
      icon: Target,
      title: 'Strategic Positioning',
      description: 'Discover what makes your advisory practice unique',
    },
    {
      icon: FileText,
      title: 'Custom Scripts',
      description: 'Get AI-generated webinar content tailored to you',
    },
    {
      icon: Sparkles,
      title: 'Marketing Materials',
      description: 'Emails, social posts, and follow-up sequences',
    },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8 relative">
      {/* Top nav */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute top-6 right-6 flex items-center gap-2"
      >
        <button
          onClick={() => navigate('/history')}
          className="p-2 rounded-xl text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 transition-colors"
          title="My Webinars"
        >
          <Library className="w-5 h-5" />
        </button>
        <button
          onClick={() => navigate('/settings')}
          className="p-2 rounded-xl text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 transition-colors"
          title="Settings"
        >
          <Settings className="w-5 h-5" />
        </button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl space-y-8"
      >
        <div className="text-center space-y-4">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm"
          >
            <Sparkles className="w-4 h-4" />
            <span>Webinar Content Generator</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl md:text-5xl font-bold text-slate-100"
          >
            Create Your Perfect{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-accent-400">
              Webinar
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-lg text-slate-400 max-w-xl mx-auto"
          >
            Answer 6 strategic questions about your advisory practice, and we'll
            generate a complete webinar package customized to your unique
            approach.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6 pb-6 border-b border-slate-700/50">
              <div className="w-10 h-10 rounded-xl bg-primary-500/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="text-slate-200 font-medium">
                  Takes about 10-15 minutes
                </p>
                <p className="text-sm text-slate-500">
                  Your answers are saved automatically
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-4 h-4 text-primary-400" />
                  </div>
                  <div>
                    <p className="text-slate-200 font-medium">{feature.title}</p>
                    <p className="text-sm text-slate-500">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <Button
                size="lg"
                className="w-full"
                onClick={() => navigate('/question/1')}
              >
                Start Questionnaire
                <ArrowRight className="w-5 h-5" />
              </Button>
            </motion.div>
          </Card>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center text-sm text-slate-600"
        >
          Your responses are stored locally and never shared without your
          permission.
        </motion.p>
      </motion.div>
    </div>
  );
}
